/**
 * EventsLandingPage — the public events index in the FWM premium house style:
 * a full-bleed photograph of the gathering itself under a dark scrim, the
 * stats lifted over it, then the calendar on warm cream.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { eventAPI } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { MapPin, ArrowRight, CalendarDays, Radio, Star } from 'lucide-react';
import FwmShell from '@/components/fwm/FwmShell';
import { FwmHero, FwmStatStrip } from '@/components/fwm/FwmHero';
import { FwmPhotoCard } from '@/components/fwm/FwmPhotoCard';
import { useHero } from '@/components/fwm/useHero';
import { FWM_IMAGERY, resolveImage } from '@/components/fwm/imagery';

const dateLine = (event) => (event.start_date
  ? new Date(event.start_date).toLocaleDateString(undefined,
    { day: 'numeric', month: 'short', year: 'numeric' })
  : 'Dates to follow');

const EventCard = ({ event, live, index }) => {
  const venue = event.venue?.name || event.venue_name;
  return (
    <FwmPhotoCard
      testId={`events-card-${event.id}`}
      to={`/event/${event.slug || event.id}`}
      image={event.branding?.hero_image_url || event.banner_url || event.cover_image_url}
      index={index}
      delay={index * 70}
      overline={`${venue ? `${venue} · ` : ''}${dateLine(event)}`}
      badge={live ? <><Radio className="h-3 w-3" /> Happening now</> : null}
      title={event.name}
      blurb={event.description}
      meta={venue ? <><MapPin className="h-3 w-3" /> {venue}</> : null}
      cta={live ? 'Register — still open' : 'Register now'}
    />
  );
};

const FeaturedEvent = ({ event, onOpen }) => {
  const venue = event.venue?.name || event.venue_name;
  const art = resolveImage(event.branding?.hero_image_url || event.banner_url
    || event.cover_image_url) || FWM_IMAGERY.heroEvents;
  return (
    <article className="fwm-rise grid lg:grid-cols-[1.15fr_1fr] overflow-hidden rounded-2xl
                        border border-[color:var(--fwm-line)] bg-white shadow-sm"
             data-testid={`events-featured-card-${event.id}`}>
      <button type="button" onClick={onOpen}
              className="relative block aspect-[16/10] lg:aspect-auto lg:min-h-[22rem]
                         overflow-hidden group">
        <img src={art} alt={event.name}
             className="absolute inset-0 h-full w-full object-cover transition-transform
                        duration-700 group-hover:scale-[1.04]" />
        <span className="absolute left-5 top-5 inline-flex items-center gap-1.5 rounded-full
                         bg-[#1d1b16]/85 px-3 py-1.5 text-[0.7rem] font-semibold uppercase
                         tracking-[0.18em] text-[#e8c46a] backdrop-blur">
          <Star className="h-3 w-3 fill-[#e8c46a]" /> Featured
        </span>
      </button>
      <div className="p-8 lg:p-12 flex flex-col justify-center text-left">
        <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--fwm-muted)]">
          {venue ? `${venue} · ` : ''}{dateLine(event)}
        </p>
        <h3 className="fwm-display mt-4 text-2xl sm:text-3xl tracking-tight leading-tight">
          {event.name}
        </h3>
        {event.description && (
          <p className="mt-4 text-base text-[color:var(--fwm-body)] leading-relaxed line-clamp-4">
            {event.description}
          </p>
        )}
        <div className="mt-8">
          <button className="fwm-btn" onClick={onOpen}
                  data-testid={`events-featured-cta-${event.id}`}>
            Register now <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </article>
  );
};

const EventsLandingPage = () => {
  const navigate = useNavigate();
  const { data: events, isLoading } = useQuery({
    queryKey: ['public-events'],
    queryFn: () => eventAPI.list(true).then((r) => r.data || r),
    // The public page has to answer to the admin desk: a featured flag flipped
    // or an event deleted shows up here within half a minute, with no reload.
    staleTime: 15000,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });

  const now = new Date();
  const active = (Array.isArray(events) ? events : []).filter((e) => {
    const end = e.end_date ? new Date(e.end_date) : null;
    return end ? end >= now : true;
  });
  const featured = active.filter((e) => e.is_featured);
  const live = active.filter((e) => e.start_date && new Date(e.start_date) <= now);
  const ongoing = live.filter((e) => !e.is_featured);
  const upcoming = active.filter((e) => !live.includes(e) && !e.is_featured);
  const cities = new Set(active.map((e) => e.venue?.city || e.venue?.name || e.venue_name)
    .filter(Boolean));
  const heroPoster = resolveImage(featured[0]?.branding?.hero_image_url
    || upcoming[0]?.branding?.hero_image_url) || FWM_IMAGERY.heroEvents;
  const hero = useHero('events', { image_url: heroPoster });

  return (
    <FwmShell ctaLabel="Browse events" ctaLink="/events" testId="events-landing" overDark>
      <FwmHero
        testId="events-hero"
        image={hero.image_url}
        fallbackImage={hero.fallback_image_url}
        video={hero.video_url}
        mediaPriority={hero.media_priority}
        eyebrow={hero.eyebrow}
        title={hero.title}
        accent={hero.accent}
        blurb={hero.blurb}
        actions={(
          <>
            <button className="fwm-btn fwm-btn-lg" data-testid="browse-events-btn"
                    onClick={() => document.getElementById('events')
                      ?.scrollIntoView({ behavior: 'smooth' })}>
              Browse events <ArrowRight className="h-4 w-4" />
            </button>
            <button className="fwm-btn-ondark" data-testid="events-host-btn"
                    onClick={() => navigate('/login')}>
              Host a gathering
            </button>
          </>
        )}
      />

      <FwmStatStrip testId="events-stats" stats={[
        [active.length, 'Gatherings listed'],
        [live.length, 'Happening now'],
        [active.length - live.length, 'Still to come'],
        [cities.size || '—', 'Cities'],
      ]} />

      {featured.length > 0 && (
        <section className="fwm-section !pb-0" data-testid="events-featured">
          <div className="fwm-section-head">
            <h2>Featured</h2>
            <p className="text-sm text-[color:var(--fwm-muted)]">
              {featured.length > 1 ? 'Hand-picked by the ministry' : 'Don’t miss this one'}
            </p>
          </div>
          <div className="space-y-8">
            {featured.map((e) => (
              <FeaturedEvent key={e.id} event={e}
                             onOpen={() => navigate(`/event/${e.slug || e.id}`)} />
            ))}
          </div>
        </section>
      )}

      {ongoing.length > 0 && (
        <section className="fwm-section !pb-0" data-testid="events-ongoing">
          <div className="fwm-section-head">
            <h2>Happening now</h2>
            <p className="text-sm text-[color:var(--fwm-muted)]">Registration may still be open</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {ongoing.map((e, i) => <EventCard key={e.id} event={e} live index={i} />)}
          </div>
        </section>
      )}

      <section id="events" className="fwm-section" data-testid="events-upcoming">
        <div className="fwm-section-head">
          <h2>On the calendar</h2>
          <span className="text-sm text-[color:var(--fwm-muted)] tabular-nums">
            {upcoming.length}
          </span>
        </div>
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-80 rounded-xl" />)}
          </div>
        ) : upcoming.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {upcoming.map((e, i) => <EventCard key={e.id} event={e} index={i} />)}
          </div>
        ) : ongoing.length === 0 && featured.length === 0 && (
          <div className="border border-[color:var(--fwm-line)] rounded-xl px-8 py-16 text-center"
               data-testid="events-empty">
            <CalendarDays className="h-8 w-8 mx-auto text-[color:var(--fwm-muted)]" />
            <p className="mt-5 text-[color:var(--fwm-body)]">
              Nothing is on the calendar just yet — new gatherings are published here first.
            </p>
          </div>
        )}
      </section>

      <section className="border-t border-[color:var(--fwm-line)]">
        <div className="fwm-section !py-20 flex flex-col lg:flex-row lg:items-center
                        justify-between gap-10">
          <div className="max-w-xl">
            <h2 className="fwm-display text-3xl sm:text-4xl tracking-tight">
              Hosting a gathering?
            </h2>
            <p className="mt-4 text-[color:var(--fwm-body)] leading-relaxed">
              Registration, badges, check-in, meals, housing and reports — the same tools the
              national convention runs on are open to every assembly.
            </p>
          </div>
          <button className="fwm-btn-ghost fwm-btn-lg shrink-0" data-testid="events-organiser-cta"
                  onClick={() => navigate('/login')}>
            Start planning <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>
    </FwmShell>
  );
};

export default EventsLandingPage;
